// usePlaybookProgress — persist the user's current playbook session to
// localStorage so they can resume from the home page after closing the
// runner, navigating away, or refreshing the tab.
//
// Why localStorage (not Supabase): the home runner is anonymous-friendly
// (no sign-up required). Progress is a UX nicety, not data we need to
// share across devices. Keeping it client-side means zero backend cost
// and zero auth friction.
//
// Stored shape: slug + topic + which steps the user has run + per-step
// model choices + (optionally) the chat transcript so the user can pick
// the conversation back up exactly where they left off. The transcript
// is capped (see TRANSCRIPT_MAX_TURNS) to keep localStorage usage sane,
// and we strip out any in-flight streaming flags before persisting.
import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "hl.playbook.progress.v2";
const LEGACY_KEYS = ["hl.playbook.progress.v1"];
// 7 days — beyond that, the saved session is stale enough that a fresh
// start is friendlier than a "resume something you forgot about" prompt.
const TTL_MS = 7 * 24 * 60 * 60 * 1000;
// Cap how many turns we keep in localStorage. Real chats can balloon
// past 5MB quota fast — we keep the most recent N turns which is plenty
// to feel like a resume without nuking the user's quota.
// Exported so the resume UI can show "X recent turns saved" and warn
// the user that older messages may not be available.
export const TRANSCRIPT_MAX_TURNS = 40;

/**
 * Mirror of the runner's ChatTurn union, but typed loosely so this hook
 * doesn't need to import from the component (avoids a circular dep).
 * The runner casts when reading.
 */
export type PersistedTurn = Record<string, unknown> & { kind: string; id: string };

export type PlaybookProgress = {
  slug: string;
  topic: string;
  /** Indexes of steps the user has triggered at least once. */
  completedSteps: number[];
  /** Per-step model selection (overrides default playbook recommendation). */
  stepModels: string[];
  /** Last model the user was actively chatting with. */
  lastModel: string;
  /** Chat transcript snapshot (optional — older saves won't have it). */
  turns?: PersistedTurn[];
  /**
   * Short stable hash of the playbook's step shape (count + per-step
   * title + recommended model) at save time. The runner compares it to
   * the current playbook's fingerprint on resume — if they differ, the
   * persisted indices/models can no longer be trusted and the saved
   * row is treated as stale (wiped, no resume prompt). Optional for
   * back-compat with rows written before this field existed.
   */
  fingerprint?: string;
  updatedAt: number;
};

/**
 * Compute a short stable fingerprint for a playbook's step shape.
 * Used by the runner to detect when the underlying playbook content has
 * changed since the user's last save (step added, removed, or reordered;
 * recommended model swapped). We hash count + per-step `title|model`
 * with a small DJB2-style integer hash and base36-encode it — small
 * enough to ship in localStorage, stable across reloads, and good
 * enough collision-wise for our handful of steps per playbook.
 */
export function playbookFingerprint(steps: Array<{ title: string; model: string }>): string {
  const seed = `${steps.length}|` + steps.map((s) => `${s.title}::${s.model}`).join("§");
  let h = 5381;
  for (let i = 0; i < seed.length; i++) {
    h = ((h << 5) + h + seed.charCodeAt(i)) | 0;
  }
  // Convert to unsigned then base36 for a compact stable string.
  return (h >>> 0).toString(36);
}

function read(): PlaybookProgress | null {
  if (typeof window === "undefined") return null;
  try {
    let raw = window.localStorage.getItem(STORAGE_KEY);
    // Migrate from older key versions if needed (one-shot copy + cleanup).
    if (!raw) {
      for (const k of LEGACY_KEYS) {
        const legacy = window.localStorage.getItem(k);
        if (legacy) {
          raw = legacy;
          window.localStorage.setItem(STORAGE_KEY, legacy);
          window.localStorage.removeItem(k);
          break;
        }
      }
    }
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PlaybookProgress;
    if (!parsed?.slug || typeof parsed.updatedAt !== "number") return null;
    if (Date.now() - parsed.updatedAt > TTL_MS) {
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function write(p: PlaybookProgress | null) {
  if (typeof window === "undefined") return;
  try {
    if (p === null) window.localStorage.removeItem(STORAGE_KEY);
    else window.localStorage.setItem(STORAGE_KEY, JSON.stringify(p));
  } catch {
    /* quota / privacy mode — try a slimmer write without the transcript */
    if (p && p.turns) {
      try {
        const slim = { ...p, turns: undefined };
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(slim));
      } catch {
        /* still no luck — give up silently */
      }
    }
  }
}

/** Trim a transcript to the most recent N turns and strip volatile flags. */
export function sanitizeTurns(turns: PersistedTurn[] | undefined): PersistedTurn[] {
  if (!Array.isArray(turns) || turns.length === 0) return [];
  const recent = turns.slice(-TRANSCRIPT_MAX_TURNS);
  return recent.map((t) => {
    const clone = { ...t } as Record<string, unknown>;
    // Never persist mid-stream state — on resume everything is "done".
    if (clone.kind === "assistant") {
      clone.streaming = false;
    }
    if (clone.kind === "compare") {
      clone.streaming = [false, false];
    }
    return clone as PersistedTurn;
  });
}

export function usePlaybookProgress() {
  const [progress, setProgress] = useState<PlaybookProgress | null>(() => read());

  // Listen for changes from other tabs so the resume card stays in sync
  // if the user has the home page open in two windows.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setProgress(read());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const save = useCallback((next: Omit<PlaybookProgress, "updatedAt">) => {
    const row: PlaybookProgress = {
      ...next,
      turns: sanitizeTurns(next.turns),
      updatedAt: Date.now(),
    };
    write(row);
    setProgress(row);
  }, []);

  const clear = useCallback(() => {
    write(null);
    setProgress(null);
  }, []);

  return { progress, save, clear };
}
